/**
 * Runtime Variable Resolver
 *
 * Resolves template variables to actual values from the triggering resource
 * and its related resources (Patient, Practitioner, Appointment, Organization, etc.).
 *
 * This is the single source of truth for variable resolution at email send time.
 * The variable catalog in notification-variables.ts mirrors this for the UI picker.
 *
 * Organization resolution priority (practice.* and provider.* variables):
 *   1. resource.managingOrganization (EpisodeOfCare, Location, etc.)
 *   2. Appointment Practitioner participant → Practitioner.extension[ADMIN_EXT_URLS.organization]
 *   3. Task owner/performer/requester → same Practitioner path
 *   4. Patient's active CareTeam primary participant (fallback for trigger
 *      resources with no direct practitioner link, e.g. QuestionnaireResponse)
 *
 * Billing variable source: DocumentReference.context.period (start/end). If trigger is
 * an Invoice, Invoice.period is used instead. See docs/agentic/medplum/email-templates.md (Known limitations).
 *
 * recipient.firstName: falls back to patient.firstName when no distinct recipient
 * profile is available in context. See docs/agentic/medplum/email-templates.md (Known limitations).
 */
import type { MedplumClient } from '@medplum/core';
import type { Location, Task } from '@medplum/fhirtypes';
import type {
  NotificationTemplateConfig,
  NotificationTemplateVariable,
} from '../notification-template.types';
import type { NotificationEventContext } from '../notification-automation.types';
import {
  collectVariableKeysFromTemplate,
  renderTemplateString,
  buildExampleValueMap,
} from './notification-template-renderer';
import { resolveFieldPath } from './notification-trigger-evaluator';
import {
  ADMIN_EXT_URLS,
  VIDEO_MEETING_URL_EXT,
  RECURRING_PATTERN_EXT,
  QUALIFICATION_EXT_URLS,
  PRACTICE_CANCELLATION_WINDOW_HOURS_EXT,
  PRACTICE_EMAIL_RESPONSE_TIMEFRAME_EXT,
  PRACTICE_SCHEDULE_LINK_EXT,
  MISSED_SESSION_FEE_EXT,
} from '@unify/fhir-model/constants';
import {
  getLocationArrivalInstructions,
  getLocationDisplayAddress,
  getLocationParkingInstructions,
} from '@unify/fhir-model/resources/location';
import { DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE } from '@unify/fhir-model/resources/schedule';
import {
  buildClientPortalScheduleUrl,
  buildClientSignInUrl,
} from '@/lib/clientPortalRoutes';
import { getPublicAppBaseUrl, resolvePublicAppBaseUrl } from '@/lib/publicAppBaseUrl';
import { getTaskLinkedAppointmentId } from '@/lib/taskLinkedAppointment';
import { formatUsTimeZoneAbbreviation } from '@/lib/usTimeZoneLabel';
import { getZoomJoinHref } from '@/lib/zoomLinks';

/**
 * Appointment instants must be formatted against an explicit zone: emails render
 * inside the rules-engine bot (Lambda, TZ=UTC), where the runtime default would
 * print UTC wall clock. Practice zone, same default as availability authoring.
 */
const NOTIFICATION_TIME_ZONE = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE;

/** Subject/body/text + variables — enough to build preview or test-send value maps. */
export type TemplateMacroFields = Pick<
  NotificationTemplateConfig,
  'subject' | 'htmlBody' | 'textBody' | 'variables'
>;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResourceCache {
  [key: string]: Record<string, unknown> | undefined;
}

// ---------------------------------------------------------------------------
// Variable resolution map
// ---------------------------------------------------------------------------

type VariableResolverFn = (
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
) => Promise<string | undefined>;

// Human-readable labels for recurring appointment patterns (RECURRING_PATTERN_EXT valueCode).
const RECURRENCE_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

const BUILTIN_RESOLVERS: Record<string, VariableResolverFn> = {
  // ─── Patient ────────────────────────────────────────────────────────
  'patient.firstName': async (event, cache, medplum) => {
    const patient = await resolvePatient(event, cache, medplum);
    if (!patient) return undefined;
    const name = (patient.name as Array<{ given?: string[] }> | undefined)?.[0];
    return name?.given?.[0];
  },
  'patient.lastName': async (event, cache, medplum) => {
    const patient = await resolvePatient(event, cache, medplum);
    if (!patient) return undefined;
    const name = (patient.name as Array<{ family?: string }> | undefined)?.[0];
    return name?.family;
  },
  'patient.fullName': async (event, cache, medplum) => {
    const patient = await resolvePatient(event, cache, medplum);
    if (!patient) return undefined;
    const name = (patient.name as Array<{ given?: string[]; family?: string }> | undefined)?.[0];
    const given = name?.given?.join(' ') ?? '';
    const family = name?.family ?? '';
    return [given, family].filter(Boolean).join(' ') || undefined;
  },
  'patient.email': async (event, cache, medplum) => {
    const patient = await resolvePatient(event, cache, medplum);
    if (!patient) return undefined;
    return extractEmail(patient);
  },

  // ─── Recipient (falls back to patient when no distinct recipient profile is in context) ──
  // See docs/agentic/medplum/email-templates.md for known limitation with guarantors.
  'recipient.firstName': async (event, cache, medplum) => {
    const patient = await resolvePatient(event, cache, medplum);
    if (!patient) return undefined;
    const name = (patient.name as Array<{ given?: string[] }> | undefined)?.[0];
    return name?.given?.[0];
  },

  // ─── Portal ──────────────────────────────────────────────────────────
  // Source: APP_BASE_URL / VITE_APP_URL (or hosted project inference) + /signin.
  'portal.link': async (_event, _cache, medplum) =>
    buildClientSignInUrl(await resolvePublicAppBaseUrl(medplum)),

  // ─── Practice (from resolved Organization) ───────────────────────────
  // Priority: resource.managingOrganization → Practitioner.extension[org] → Task performer's org.
  'practice.name': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    return (org?.name as string | undefined) || undefined;
  },
  'practice.email': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    return extractTelecom(org, 'email');
  },
  'practice.phone': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    return extractTelecom(org, 'phone');
  },
  'practice.websiteUrl': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    return extractTelecom(org, 'url');
  },
  'practice.officeAddress': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const addresses = org.address as Array<{
      line?: string[];
      city?: string;
      state?: string;
      postalCode?: string;
    }> | undefined;
    const addr = addresses?.[0];
    if (!addr) return undefined;
    const parts = [
      ...(addr.line ?? []),
      [addr.city, addr.state].filter(Boolean).join(', '),
      addr.postalCode,
    ].filter(Boolean);
    return parts.join(', ') || undefined;
  },
  'practice.addressLine1': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const addresses = org.address as Array<{ line?: string[] }> | undefined;
    const lines = addresses?.[0]?.line ?? [];
    return lines.join(', ') || undefined;
  },
  'practice.addressLine2': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const addresses = org.address as Array<{
      city?: string;
      state?: string;
      postalCode?: string;
    }> | undefined;
    const addr = addresses?.[0];
    if (!addr) return undefined;
    const parts = [
      [addr.city, addr.state].filter(Boolean).join(', '),
      addr.postalCode,
    ].filter(Boolean);
    return parts.join(' ') || undefined;
  },
  'practice.mapLink': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const addresses = org.address as Array<{
      line?: string[];
      city?: string;
      state?: string;
      postalCode?: string;
    }> | undefined;
    const addr = addresses?.[0];
    if (!addr) return undefined;
    const parts = [
      ...(addr.line ?? []),
      addr.city,
      addr.state,
      addr.postalCode,
    ].filter(Boolean);
    if (!parts.length) return undefined;
    const query = encodeURIComponent(parts.join(', '));
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  },
  'practice.scheduleLink': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (org) {
      const exts = org.extension as Array<{ url: string; valueUrl?: string; valueString?: string }> | undefined;
      const custom = exts?.find((e) => e.url === PRACTICE_SCHEDULE_LINK_EXT);
      if (custom?.valueUrl) return custom.valueUrl;
      if (custom?.valueString) return custom.valueString;
    }
    return buildClientPortalScheduleUrl(getPublicAppBaseUrl());
  },
  'practice.cancellationWindowHours': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const exts = org.extension as Array<{
      url: string;
      valueInteger?: number;
      valueUnsignedInt?: number;
      valueString?: string;
    }> | undefined;
    const ext = exts?.find((e) => e.url === PRACTICE_CANCELLATION_WINDOW_HOURS_EXT);
    if (!ext) return undefined;
    const val = ext.valueInteger ?? ext.valueUnsignedInt;
    if (val != null) return String(val);
    return ext.valueString || undefined;
  },
  'practice.emailResponseTimeframe': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const exts = org.extension as Array<{ url: string; valueString?: string }> | undefined;
    return exts?.find((e) => e.url === PRACTICE_EMAIL_RESPONSE_TIMEFRAME_EXT)?.valueString || undefined;
  },

  // ─── Provider / Practitioner ─────────────────────────────────────────
  'provider.name': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    const name = (pract.name as Array<{ given?: string[]; family?: string; prefix?: string[] }> | undefined)?.[0];
    const parts = [
      ...(name?.prefix ?? []),
      ...(name?.given ?? []),
      name?.family,
    ].filter(Boolean);
    return parts.join(' ') || undefined;
  },
  'provider.firstName': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    const name = (pract.name as Array<{ given?: string[] }> | undefined)?.[0];
    return name?.given?.[0];
  },
  'provider.credentials': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    const qualifications = pract.qualification as Array<{ code?: { text?: string } }> | undefined;
    return qualifications?.map((q) => q.code?.text).filter(Boolean).join(', ') || undefined;
  },
  // Source: primary active Practitioner.qualification identifier value.
  'provider.licenseNumber': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    return extractPrimaryLicenseNumber(pract);
  },
  // Source: primary active Practitioner.qualification.code.text or coding.display.
  'provider.licenseTitle': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    return extractPrimaryLicenseTitle(pract);
  },
  // Source: Practitioner.telecom where system=phone.
  'provider.phone': async (event, cache, medplum) => {
    const pract = await resolvePractitioner(event, cache, medplum);
    if (!pract) return undefined;
    return extractTelecom(pract, 'phone');
  },

  // ─── Appointment ─────────────────────────────────────────────────────
  'appointment.date': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const start = appointment.start as string | undefined;
    if (!start) return undefined;
    try {
      return new Date(start).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: NOTIFICATION_TIME_ZONE });
    } catch {
      return start;
    }
  },
  'appointment.time': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const start = appointment.start as string | undefined;
    if (!start) return undefined;
    try {
      const time = new Date(start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: NOTIFICATION_TIME_ZONE });
      return `${time} ${formatUsTimeZoneAbbreviation(NOTIFICATION_TIME_ZONE)}`;
    } catch {
      return start;
    }
  },
  'appointment.type': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const type = appointment.appointmentType as
      | { text?: string; coding?: Array<{ display?: string }> }
      | undefined;
    return type?.text || type?.coding?.[0]?.display || undefined;
  },
  'appointment.location': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const participant = appointment.participant as
      | Array<{ actor?: { display?: string }; type?: Array<{ coding?: Array<{ code?: string }> }> }>
      | undefined;
    const location = participant?.find((p) =>
      p.type?.some((t) => t.coding?.some((c) => c.code === 'LOC')),
    );
    return location?.actor?.display || undefined;
  },
  // ─── Appointment location (in-person office instructions) ────────────
  // Source: Location resource referenced by the Appointment's LOC participant.
  'appointment.locationName': async (event, cache, medplum) => {
    const location = await resolveAppointmentLocation(event, cache, medplum);
    return location?.name;
  },
  // Display address override extension, else the formatted Location address line.
  'appointment.locationAddress': async (event, cache, medplum) => {
    const location = await resolveAppointmentLocation(event, cache, medplum);
    if (!location) return undefined;
    return getLocationDisplayAddress(location);
  },
  // Falls back to a generic, safe message when the Location has no custom parking instructions.
  'appointment.locationParkingInstructions': async (event, cache, medplum) => {
    const location = await resolveAppointmentLocation(event, cache, medplum);
    if (!location) return undefined;
    return (
      getLocationParkingInstructions(location) ??
      'Specific parking instructions are not available for this location — please allow a few extra minutes if you need to find parking.'
    );
  },
  // Falls back to a generic, safe message when the Location has no custom arrival instructions.
  'appointment.locationArrivalInstructions': async (event, cache, medplum) => {
    const location = await resolveAppointmentLocation(event, cache, medplum);
    if (!location) return undefined;
    return (
      getLocationArrivalInstructions(location) ??
      'Please allow a few minutes to check in when you arrive.'
    );
  },
  // Source: Appointment.extension[VIDEO_MEETING_URL_EXT].valueUrl.
  // Unresolvable unless it is a real Zoom join URL — templates mark this variable
  // required so a malformed value suppresses the send instead of emailing a dead link.
  'appointment.videoLink': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const exts = appointment.extension as Array<{ url: string; valueUrl?: string }> | undefined;
    return getZoomJoinHref(exts?.find((e) => e.url === VIDEO_MEETING_URL_EXT)?.valueUrl);
  },
  // Source: MISSED_SESSION_FEE_EXT on Organization (formatted currency string, e.g. "$150").
  'appointment.missedSessionFee': async (event, cache, medplum) => {
    const org = await resolveOrganization(event, cache, medplum);
    if (!org) return undefined;
    const exts = org.extension as Array<{ url: string; valueString?: string }> | undefined;
    return exts?.find((e) => e.url === MISSED_SESSION_FEE_EXT)?.valueString || undefined;
  },
  // Source: RECURRING_PATTERN_EXT valueCode formatted as human text.
  'appointment.recurrence': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const exts = appointment.extension as Array<{ url: string; valueCode?: string }> | undefined;
    const code = exts?.find((e) => e.url === RECURRING_PATTERN_EXT)?.valueCode;
    if (!code) return undefined;
    return RECURRENCE_LABELS[code] ?? code;
  },
  // Source: Appointment.start (first appointment in a recurrence series).
  'appointment.recurrenceStartDate': async (event, cache, medplum) => {
    const appointment = await resolveAppointmentForEvent(event, cache, medplum);
    if (!appointment) return undefined;
    const start = appointment.start as string | undefined;
    if (!start) return undefined;
    try {
      return new Date(start).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: NOTIFICATION_TIME_ZONE });
    } catch {
      return start;
    }
  },
  // Source: no FHIR field for series end date — returns undefined (optional variable).
  // See docs/agentic/medplum/email-templates.md.
  'appointment.recurrenceEndDate': async () => undefined,

  // ─── Calendar links (computed from Appointment) ──────────────────────
  'calendar.googleLink': async (event, cache, medplum) => {
    return buildGoogleCalendarLink(event, cache, medplum);
  },
  'calendar.outlookLink': async (event, cache, medplum) => {
    return buildOutlookCalendarLink(event, cache, medplum);
  },
  'calendar.icloudLink': async (event, cache, medplum) => {
    return buildICalLink(event, cache, medplum);
  },

  // ─── Document ────────────────────────────────────────────────────────
  // Source: Task.focus / Task.input references to Questionnaire or DocumentReference.
  // See docs/agentic/medplum/email-templates.md for known limitation.
  'document.requestNames': async (event, cache, medplum) => {
    return resolveDocumentRequestNames(event, cache, medplum);
  },

  // ─── Billing ─────────────────────────────────────────────────────────
  // Source: DocumentReference.context.period.start, or Invoice.period.start.
  // See docs/agentic/medplum/email-templates.md for trigger resource assumption.
  'billing.startDate': async (event) => {
    return resolveBillingDate(event, 'start');
  },
  'billing.endDate': async (event) => {
    return resolveBillingDate(event, 'end');
  },

  // ─── Inquiry ─────────────────────────────────────────────────────────
  // Source: ServiceRequest.authoredOn or QuestionnaireResponse.authored.
  'inquiry.submittedDate': async (event) => {
    const authored = (
      resolveFieldPath(event.currentResource, 'authoredOn') ??
      resolveFieldPath(event.currentResource, 'authored')
    ) as string | undefined;
    if (!authored) return undefined;
    try {
      return new Date(authored).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return authored;
    }
  },

  // ─── Episode ─────────────────────────────────────────────────────────
  'episode.status': async (event) => {
    return resolveFieldPath(event.currentResource, 'status') as string | undefined;
  },

  // ─── Task ────────────────────────────────────────────────────────────
  'task.description': async (event) => {
    return resolveFieldPath(event.currentResource, 'description') as string | undefined;
  },
  'task.status': async (event) => {
    return resolveFieldPath(event.currentResource, 'status') as string | undefined;
  },
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Placeholder values for built-in catalog keys when there is no FHIR event (preview / test send). */
export const BUILTIN_VAR_PREVIEW_DEFAULTS: Record<string, string> = {
  // Patient
  'patient.firstName': 'Jane',
  'patient.lastName': 'Doe',
  'patient.fullName': 'Jane Doe',
  'patient.email': 'patient@example.com',
  // Recipient
  'recipient.firstName': 'Jane',
  // Portal
  'portal.link': 'http://localhost:3015/signin',
  // Practice
  'practice.name': 'Unify Mental Health',
  'practice.email': 'support@unifymentalhealth.com',
  'practice.phone': '(555) 555-5555',
  'practice.websiteUrl': 'https://unifymentalhealth.com',
  'practice.officeAddress': '123 Wellness St, Suite 200, New York, NY 10001',
  'practice.addressLine1': '123 Wellness St, Suite 200',
  'practice.addressLine2': 'New York, NY 10001',
  'practice.mapLink': 'https://www.google.com/maps/search/?api=1&query=123+Wellness+St%2C+New+York%2C+NY',
  'practice.scheduleLink': 'https://app.unifymentalhealth.com/client-portal/request-service',
  'practice.cancellationWindowHours': '24',
  'practice.emailResponseTimeframe': '1–2 business days',
  // Provider
  'provider.name': 'Dr. Alex Smith',
  'provider.firstName': 'Alex',
  'provider.credentials': 'LCSW',
  'provider.licenseNumber': '123456',
  'provider.licenseTitle': 'LCSW',
  'provider.phone': '(555) 555-5555',
  // Appointment
  'appointment.date': 'Wednesday, June 15, 2026',
  'appointment.time': '2:30 PM',
  'appointment.type': 'Follow-up',
  'appointment.location': 'Virtual',
  'appointment.locationName': 'Downtown Office',
  'appointment.locationAddress': '123 Wellness St, Suite 200, New York, NY 10001',
  'appointment.locationParkingInstructions': 'Metered street parking is available in front of the building.',
  'appointment.locationArrivalInstructions': 'Please check in at the front desk when you arrive.',
  'appointment.videoLink': 'https://zoom.us/j/123456789',
  'appointment.missedSessionFee': '$150',
  'appointment.recurrence': 'Weekly',
  'appointment.recurrenceStartDate': 'June 1, 2026',
  // Calendar
  'calendar.googleLink': 'https://www.google.com/calendar/render?action=TEMPLATE',
  'calendar.outlookLink': 'https://outlook.live.com/calendar/0/deeplink/compose',
  'calendar.icloudLink': 'data:text/calendar;charset=utf8,BEGIN%3AVCALENDAR',
  // Document
  'document.requestNames': 'Intake form, Consent form',
  // Billing
  'billing.startDate': 'May 1, 2026',
  'billing.endDate': 'May 31, 2026',
  // Inquiry
  'inquiry.submittedDate': 'June 15, 2026',
  // Episode / Task
  'episode.status': 'active',
  'task.description': 'Sample task',
  'task.status': 'ready',
};

/**
 * Example + built-in default values for preview and test email when no FHIR trigger context exists.
 */
export function buildTestOrPreviewValueMap(
  config: TemplateMacroFields,
  sampleContext?: Record<string, string>,
): Record<string, string> {
  const sample = sampleContext ?? {};
  const out: Record<string, string> = {
    ...buildExampleValueMap(config.variables),
    ...sample,
  };
  const keys = collectVariableKeysFromTemplate({
    subject: config.subject,
    htmlBody: config.htmlBody,
    textBody: config.textBody ?? '',
    variables: config.variables,
  });
  for (const key of keys) {
    if (out[key]) continue;
    const fallback = BUILTIN_VAR_PREVIEW_DEFAULTS[key];
    if (fallback) out[key] = fallback;
  }
  // Sidecar `example` values are env-specific leftovers (often the last upload host).
  // Test-send / preview must follow this SPA, not the ActivityDefinition example.
  if (!sample['portal.link']) {
    out['portal.link'] = buildClientSignInUrl(getPublicAppBaseUrl());
  }
  if (!sample['practice.scheduleLink']) {
    out['practice.scheduleLink'] = buildClientPortalScheduleUrl(getPublicAppBaseUrl());
  }
  return out;
}

/**
 * Same rendering as the Preview tab / test send for strings without a live Medplum event.
 */
export function renderPreviewWithCatalogDefaults(
  subject: string,
  htmlBody: string,
  textBody: string,
  variables: NotificationTemplateVariable[],
): { subject: string; htmlBody: string; textBody: string; unresolvedRequired: string[] } {
  const vals = buildTestOrPreviewValueMap({ subject, htmlBody, textBody, variables });
  const subjectResult = renderTemplateString(subject, variables, vals);
  const bodyResult = renderTemplateString(htmlBody, variables, vals);
  const textResult = renderTemplateString(textBody, variables, vals);
  return {
    subject: subjectResult.rendered,
    htmlBody: bodyResult.rendered,
    textBody: textResult.rendered,
    unresolvedRequired: [
      ...new Set([
        ...subjectResult.unresolvedRequired,
        ...bodyResult.unresolvedRequired,
        ...textResult.unresolvedRequired,
      ]),
    ],
  };
}

async function resolveOneVariableKey(
  key: string,
  variableDefs: NotificationTemplateVariable[],
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<string | undefined> {
  const v = variableDefs.find((x) => x.key === key);

  const builtin = BUILTIN_RESOLVERS[key];
  if (builtin) {
    const resolved = await builtin(event, cache, medplum);
    if (resolved !== undefined && resolved !== '') return resolved;
    return undefined;
  }

  if (v?.resourcePath) {
    const resolved = resolveFieldPath(event.currentResource, v.resourcePath);
    if (resolved !== undefined && resolved !== null) return String(resolved);
    return undefined;
  }

  const dotPath = key.includes('.') ? key.split('.').slice(1).join('.') : key;
  const direct = resolveFieldPath(event.currentResource, dotPath);
  if (direct !== undefined && direct !== null) return String(direct);
  return undefined;
}

/**
 * Resolve every variable key referenced in subject/body/text or declared on the template.
 * Catalog tokens like `{{patient.firstName}}` work even when not listed in Custom Variables.
 */
export async function resolveAllVariableValues(
  fullConfig: NotificationTemplateConfig,
  event: NotificationEventContext,
  medplum?: MedplumClient,
): Promise<Record<string, string>> {
  const keys = collectVariableKeysFromTemplate({
    subject: fullConfig.subject,
    htmlBody: fullConfig.htmlBody,
    textBody: fullConfig.textBody ?? '',
    variables: fullConfig.variables,
  });
  const cache: ResourceCache = {};
  const values: Record<string, string> = {};

  for (const key of keys) {
    const resolved = await resolveOneVariableKey(key, fullConfig.variables, event, cache, medplum);
    if (resolved !== undefined && resolved !== '') {
      values[key] = resolved;
    }
  }

  return values;
}

/**
 * Resolve variables declared on the template only (legacy helper).
 * Prefer {@link resolveAllVariableValues} for automation so body tokens are included.
 */
export async function resolveRuntimeVariables(
  variables: NotificationTemplateVariable[],
  event: NotificationEventContext,
  medplum?: MedplumClient,
): Promise<Record<string, string>> {
  const minimal: NotificationTemplateConfig = {
    id: '',
    version: '1',
    status: 'draft',
    name: '',
    channel: 'email',
    from: { mode: 'static', email: '', name: '' },
    subject: '',
    htmlBody: '',
    textBody: '',
    variables,
  };
  return resolveAllVariableValues(minimal, event, medplum);
}

// ---------------------------------------------------------------------------
// Calendar link builders
// ---------------------------------------------------------------------------

function formatICalDate(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

async function getAppointmentCalendarData(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum: MedplumClient | undefined,
): Promise<{ title: string; start: string; end: string; location?: string; description?: string } | null> {
  const appointment = await resolveAppointmentForEvent(event, cache, medplum);
  if (!appointment) return null;

  const start = appointment.start as string | undefined;
  if (!start) return null;

  const endRaw = appointment.end as string | undefined;
  const durationMinutes = appointment.minutesDuration as number | undefined;
  let end = endRaw;
  if (!end && start && durationMinutes) {
    end = new Date(new Date(start).getTime() + durationMinutes * 60_000).toISOString();
  }
  if (!end) {
    end = new Date(new Date(start).getTime() + 60 * 60_000).toISOString(); // 1h default
  }

  const videoExt = (appointment.extension as Array<{ url: string; valueUrl?: string }> | undefined)
    ?.find((e) => e.url === VIDEO_MEETING_URL_EXT)?.valueUrl;

  const title = 'Appointment';
  const location = videoExt ?? undefined;

  return { title, start, end, location };
}

async function buildGoogleCalendarLink(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum: MedplumClient | undefined,
): Promise<string | undefined> {
  const data = await getAppointmentCalendarData(event, cache, medplum);
  if (!data) return undefined;
  const fmt = formatICalDate;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: data.title,
    dates: `${fmt(data.start)}/${fmt(data.end)}`,
  });
  if (data.location) params.set('location', data.location);
  return `https://www.google.com/calendar/render?${params.toString()}`;
}

async function buildOutlookCalendarLink(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum: MedplumClient | undefined,
): Promise<string | undefined> {
  const data = await getAppointmentCalendarData(event, cache, medplum);
  if (!data) return undefined;
  const params = new URLSearchParams({
    subject: data.title,
    startdt: data.start,
    enddt: data.end,
  });
  if (data.location) params.set('location', data.location);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

async function buildICalLink(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum: MedplumClient | undefined,
): Promise<string | undefined> {
  const data = await getAppointmentCalendarData(event, cache, medplum);
  if (!data) return undefined;
  const fmt = formatICalDate;
  const uid = `${new Date(data.start).getTime()}@unifymentalhealth.com`;
  const now = fmt(new Date().toISOString());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Unify Mental Health//EN',
    'BEGIN:VEVENT',
    `DTSTART:${fmt(data.start)}`,
    `DTEND:${fmt(data.end)}`,
    `DTSTAMP:${now}`,
    `UID:${uid}`,
    `SUMMARY:${data.title}`,
  ];
  if (data.location) lines.push(`LOCATION:${data.location}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return `data:text/calendar;charset=utf8,${encodeURIComponent(lines.join('\r\n'))}`;
}

// ---------------------------------------------------------------------------
// Document request name resolver
// ---------------------------------------------------------------------------

async function resolveDocumentRequestNames(
  event: NotificationEventContext,
  _cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<string | undefined> {
  // Only attempt resolution when trigger resource is a Task
  if (event.resourceType !== 'Task') return undefined;

  const names: string[] = [];

  // Try Task.focus → Questionnaire.title or DocumentReference.description
  const focus = event.currentResource.focus as { reference?: string } | undefined;
  if (focus?.reference && medplum) {
    const parts = focus.reference.split('/');
    if (parts.length === 2) {
      const [rt, id] = parts;
      try {
        const resource = await medplum.readResource(rt as 'Questionnaire', id);
        const r = resource as unknown as Record<string, unknown>;
        const name = (r.title ?? r.description ?? r.name) as string | undefined;
        if (name) names.push(name);
      } catch { /* non-critical */ }
    }
  }

  // Try Task.input entries that reference Questionnaire or DocumentReference
  if (medplum) {
    const inputs = event.currentResource.input as Array<{
      type?: { text?: string; coding?: Array<{ code?: string }> };
      valueReference?: { reference?: string };
      valueString?: string;
    }> | undefined;
    for (const inp of inputs ?? []) {
      if (inp.valueReference?.reference) {
        const parts = inp.valueReference.reference.split('/');
        if (parts.length === 2 && (parts[0] === 'Questionnaire' || parts[0] === 'DocumentReference')) {
          try {
            const resource = await medplum.readResource(parts[0] as 'Questionnaire', parts[1]);
            const r = resource as unknown as Record<string, unknown>;
            const name = (r.title ?? r.description ?? r.name) as string | undefined;
            if (name && !names.includes(name)) names.push(name);
          } catch { /* non-critical */ }
        }
      }
    }
  }

  if (names.length === 0) return undefined;
  return names.join(', ');
}

// ---------------------------------------------------------------------------
// Billing date resolver
// ---------------------------------------------------------------------------

function resolveBillingDate(
  event: NotificationEventContext,
  which: 'start' | 'end',
): string | undefined {
  // DocumentReference.context.period
  const contextPeriod = (event.currentResource.context as
    | { period?: { start?: string; end?: string } }
    | undefined)?.period;
  if (contextPeriod) {
    const raw = which === 'start' ? contextPeriod.start : contextPeriod.end;
    if (raw) return formatDateForDisplay(raw);
  }

  // Invoice.period
  const period = event.currentResource.period as { start?: string; end?: string } | undefined;
  if (period) {
    const raw = which === 'start' ? period.start : period.end;
    if (raw) return formatDateForDisplay(raw);
  }

  return undefined;
}

function formatDateForDisplay(iso: string): string | undefined {
  try {
    // Date-only strings (YYYY-MM-DD) are parsed as UTC midnight by the JS engine,
    // which shifts to the previous day in negative-offset timezones. Treat them as
    // local noon (T12:00:00) to keep the intended calendar date in any timezone.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Provider license helpers
// ---------------------------------------------------------------------------

type FhirQualification = {
  code?: {
    text?: string;
    coding?: Array<{ display?: string; code?: string }>;
  };
  identifier?: Array<{ value?: string; system?: string }>;
  extension?: Array<{ url: string; valueBoolean?: boolean; valueCode?: string }>;
};

function getPrimaryQualification(pract: Record<string, unknown>): FhirQualification | undefined {
  const quals = pract.qualification as FhirQualification[] | undefined;
  if (!quals?.length) return undefined;
  // Prefer one marked isPrimary
  const primary = quals.find((q) =>
    q.extension?.some(
      (e) => e.url === QUALIFICATION_EXT_URLS.isPrimary && e.valueBoolean === true,
    ),
  );
  return primary ?? quals[0];
}

function extractPrimaryLicenseNumber(pract: Record<string, unknown>): string | undefined {
  const qual = getPrimaryQualification(pract);
  if (!qual) return undefined;
  return qual.identifier?.[0]?.value;
}

function extractPrimaryLicenseTitle(pract: Record<string, unknown>): string | undefined {
  const qual = getPrimaryQualification(pract);
  if (!qual) return undefined;
  return qual.code?.text || qual.code?.coding?.[0]?.display || undefined;
}

// ---------------------------------------------------------------------------
// Resource resolution helpers
// ---------------------------------------------------------------------------

async function resolveAppointmentForEvent(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<Record<string, unknown> | undefined> {
  if (cache['appointment'] !== undefined) return cache['appointment'];

  if (event.resourceType === 'Appointment') {
    cache['appointment'] = event.currentResource;
    return event.currentResource;
  }

  if (event.resourceType === 'Task' && medplum) {
    const task = event.currentResource as unknown as Task;

    const inputRaw = (task.input as Array<{ type?: { text?: string }; valueString?: string }> | undefined)
      ?.find((item) => item.type?.text === 'reschedule-details' || item.type?.text === 'appointment-request-details')
      ?.valueString;
    if (inputRaw) {
      try {
        const parsed = JSON.parse(inputRaw) as { proposedAppointmentId?: string };
        const proposedId = parsed.proposedAppointmentId?.trim();
        if (proposedId) {
          try {
            const proposed = await medplum.readResource('Appointment', proposedId);
            cache['appointment'] = proposed as unknown as Record<string, unknown>;
            return cache['appointment'];
          } catch {
            // fall through to explicit FHIR appointment references
          }
        }
      } catch {
        // fall through to explicit FHIR appointment references
      }
    }

    const linkedAppointmentId = getTaskLinkedAppointmentId(task);
    if (linkedAppointmentId) {
      try {
        const appointment = await medplum.readResource('Appointment', linkedAppointmentId);
        cache['appointment'] = appointment as unknown as Record<string, unknown>;
        return cache['appointment'];
      } catch {
        // no appointment resolved
      }
    }
  }

  cache['appointment'] = undefined;
  return undefined;
}

async function resolvePatient(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<Record<string, unknown> | undefined> {
  if (cache['patient'] !== undefined) return cache['patient'];

  // If the trigger resource IS a Patient
  if (event.resourceType === 'Patient') {
    cache['patient'] = event.currentResource;
    return event.currentResource;
  }

  // Try common patient reference paths
  const patientRef = findPatientReference(event.currentResource);
  if (patientRef && medplum) {
    try {
      const patient = await medplum.readResource('Patient', patientRef);
      cache['patient'] = patient as unknown as Record<string, unknown>;
      return cache['patient'];
    } catch {
      cache['patient'] = undefined;
      return undefined;
    }
  }

  cache['patient'] = undefined;
  return undefined;
}

/**
 * Practitioner reference from the patient's active CareTeam primary
 * participant. Fallback for trigger resources with no direct practitioner
 * link of their own (e.g. QuestionnaireResponse, a bare ServiceRequest) —
 * same resolution the Rules Engine's `care-team-primary-contact` recipient
 * policy already uses (`recipientResolver.ts`).
 */
async function resolvePractitionerViaCareTeam(
  resource: Record<string, unknown>,
  medplum: MedplumClient,
): Promise<Record<string, unknown> | undefined> {
  const patientId = findPatientReference(resource);
  if (!patientId) return undefined;

  try {
    const careTeams = await medplum.searchResources('CareTeam', {
      patient: `Patient/${patientId}`,
      status: 'active',
      _count: '1',
    });
    const careTeam = careTeams[0] as { participant?: Array<{ role?: Array<{ coding?: Array<{ code?: string }> }>; member?: { reference?: string } }> } | undefined;
    if (!careTeam) return undefined;

    const primary = careTeam.participant?.find((p) => p.role?.some((r) => r.coding?.some((c) => c.code === 'primary')))
      ?? careTeam.participant?.[0];
    const memberRef = primary?.member?.reference;
    if (!memberRef?.startsWith('Practitioner/')) return undefined;

    const practId = memberRef.replace('Practitioner/', '');
    const pract = await medplum.readResource('Practitioner', practId);
    return pract as unknown as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

async function resolvePractitioner(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<Record<string, unknown> | undefined> {
  if (cache['practitioner'] !== undefined) return cache['practitioner'];

  // If the trigger resource IS a Practitioner
  if (event.resourceType === 'Practitioner') {
    cache['practitioner'] = event.currentResource;
    return event.currentResource;
  }

  // Try common practitioner reference paths
  const practRef = findPractitionerReference(event.currentResource);
  if (practRef && medplum) {
    try {
      const pract = await medplum.readResource('Practitioner', practRef);
      cache['practitioner'] = pract as unknown as Record<string, unknown>;
      return cache['practitioner'];
    } catch {
      cache['practitioner'] = undefined;
      return undefined;
    }
  }

  // Fallback: patient's active CareTeam primary participant (covers trigger
  // resources with no direct practitioner reference, e.g. QuestionnaireResponse).
  if (medplum) {
    const viaCareTeam = await resolvePractitionerViaCareTeam(event.currentResource, medplum);
    if (viaCareTeam) {
      cache['practitioner'] = viaCareTeam;
      return viaCareTeam;
    }
  }

  cache['practitioner'] = undefined;
  return undefined;
}

/** Resolves the Location resource for the appointment's LOC participant, if any. */
async function resolveAppointmentLocation(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<Location | undefined> {
  if (cache['location'] !== undefined) return cache['location'] as unknown as Location;

  const appointment = await resolveAppointmentForEvent(event, cache, medplum);
  const locationId = findLocationReference(appointment);
  if (!locationId || !medplum) {
    cache['location'] = undefined;
    return undefined;
  }

  try {
    const location = await medplum.readResource('Location', locationId);
    cache['location'] = location as unknown as Record<string, unknown>;
    return location;
  } catch {
    cache['location'] = undefined;
    return undefined;
  }
}

function findLocationReference(resource: Record<string, unknown> | undefined): string | undefined {
  if (!resource) return undefined;
  const participants = resource.participant as
    | Array<{ actor?: { reference?: string }; type?: Array<{ coding?: Array<{ code?: string }> }> }>
    | undefined;
  const locationParticipant = participants?.find((p) =>
    p.type?.some((t) => t.coding?.some((c) => c.code === 'LOC')),
  );
  return extractReferenceId(locationParticipant?.actor?.reference, 'Location');
}

function findPatientReference(resource: Record<string, unknown>): string | undefined {
  // EpisodeOfCare.patient, Appointment.participant, Task.for, ServiceRequest.subject
  const paths = ['patient', 'subject', 'for'];
  for (const path of paths) {
    const ref = extractReferenceId(resource[path], 'Patient');
    if (ref) return ref;
  }

  // Appointment participant with patient actor
  const participants = resource.participant as Array<{ actor?: { reference?: string } }> | undefined;
  if (participants) {
    for (const p of participants) {
      if (p.actor?.reference?.startsWith('Patient/')) {
        return p.actor.reference.split('/')[1];
      }
    }
  }

  return undefined;
}

function findPractitionerReference(resource: Record<string, unknown>): string | undefined {
  const paths = ['requester', 'owner', 'performer'];
  for (const path of paths) {
    const ref = extractReferenceId(resource[path], 'Practitioner');
    if (ref) return ref;
  }

  // Appointment participant with practitioner actor
  const participants = resource.participant as Array<{ actor?: { reference?: string } }> | undefined;
  if (participants) {
    for (const p of participants) {
      if (p.actor?.reference?.startsWith('Practitioner/')) {
        return p.actor.reference.split('/')[1];
      }
    }
  }

  return undefined;
}

/**
 * Resolve the practice Organization for the event context.
 *
 * Priority (all via `resolvePractitioner`, then Practitioner.extension[ADMIN_EXT_URLS.organization],
 * except #1):
 *   1. resource.managingOrganization (EpisodeOfCare, Location, etc.)
 *   2. Appointment Practitioner participant
 *   3. Task performer/owner/requester
 *   4. Patient's active CareTeam primary participant (fallback for trigger
 *      resources with none of the above, e.g. QuestionnaireResponse) — same
 *      resolution as the Rules Engine's `care-team-primary-contact` policy.
 */
async function resolveOrganization(
  event: NotificationEventContext,
  cache: ResourceCache,
  medplum?: MedplumClient,
): Promise<Record<string, unknown> | undefined> {
  if (cache['organization'] !== undefined) return cache['organization'];

  const setCache = (v: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
    cache['organization'] = v;
    return v;
  };

  if (!medplum) return setCache(undefined);

  // Priority 1: direct managingOrganization on the trigger resource
  const directRef = extractReferenceId(event.currentResource['managingOrganization'], 'Organization');
  if (directRef) {
    try {
      const org = await medplum.readResource('Organization', directRef);
      return setCache(org as unknown as Record<string, unknown>);
    } catch { /* fall through */ }
  }

  // Priority 2: via Practitioner's organization extension
  const pract = await resolvePractitioner(event, cache, medplum);
  if (pract) {
    const practExts = pract.extension as Array<{
      url: string;
      valueReference?: { reference?: string };
    }> | undefined;
    const orgExtRef = practExts?.find((e) => e.url === ADMIN_EXT_URLS.organization)
      ?.valueReference?.reference;
    if (orgExtRef?.startsWith('Organization/')) {
      const orgId = orgExtRef.replace('Organization/', '');
      try {
        const org = await medplum.readResource('Organization', orgId);
        return setCache(org as unknown as Record<string, unknown>);
      } catch { /* fall through */ }
    }
  }

  return setCache(undefined);
}

function extractTelecom(
  resource: Record<string, unknown>,
  system: 'email' | 'phone' | 'url',
): string | undefined {
  const telecom = resource.telecom as Array<{ system?: string; value?: string }> | undefined;
  return telecom?.find((cp) => cp.system === system)?.value;
}

function extractReferenceId(value: unknown, expectedType: string): string | undefined {
  if (!value) return undefined;
  let refStr: string | undefined;
  if (typeof value === 'string') {
    refStr = value;
  } else if (typeof value === 'object' && value !== null && 'reference' in value) {
    refStr = (value as { reference?: string }).reference;
  }
  if (!refStr) return undefined;
  const [type, id] = refStr.split('/');
  if (type === expectedType && id) return id;
  return undefined;
}

function extractEmail(resource: Record<string, unknown>): string | undefined {
  const telecom = resource.telecom as Array<{ system?: string; value?: string }> | undefined;
  if (!telecom) return undefined;
  const emailCp = telecom.find((cp) => cp.system === 'email');
  return emailCp?.value;
}
